import { invoiceControlConfigByCode, InvoiceProjectByStatus, isProjectActive } from 'entities';

import { Card, Column, Link, Row, Tag } from 'components/Blocks';
import { UserAvatarGroup } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';
import { CalendarIcon } from 'components/Icons';

import { ExpectedDate, FreshdeskTicketTag, ProjectTypeTag } from 'pages/Projects/components';
import { StatusDetails } from './StatusDetails';

import { useProjectById } from 'providers/Project';

import { getFirstAndLastName } from 'utils/text';

import { cn } from 'styles';

interface SingleProjectProps {
  control: InvoiceProjectByStatus;
}

function SingleProject({ control }: SingleProjectProps) {
  const data = useProjectById(control.project.id) ?? control.project;

  const { id, name, owner, tags, status, clientName, ticket, subOwner, type, expectedDate } = data;

  return (
    <Card className="relative overflow-hidden shadow-md transition-transform duration-500 animate-in fade-in-0 slide-in-from-bottom-11 hover:scale-[1.008]">
      <div
        // side decoration
        className="absolute bottom-0 left-0 top-0 w-2"
        style={{ background: invoiceControlConfigByCode[control.status].color }}
      />

      <div
        className={cn(
          'grid grid-cols-5 items-center pl-3',
          'mid_tablet:gap-y-3',
          //
        )}
      >
        <Row gap="2" className="col-span-2 mid_tablet:col-span-3 phone:col-span-5">
          <Column gap="1" className="max-w-[90%]">
            <Row flexWrap="wrap" gap="1">
              <Link
                to="projectProfile"
                params={{ projectId: id }}
                className="text-lg font-medium hover:underline phone:text-base"
              >
                {name.replace(/_/g, ' ')}
              </Link>

              {tags
                ?.slice()
                .sort((a, b) => a.name.length - b.name.length)
                .map((tag) => <Tag key={`${id}-${tag.id}`} {...tag} />)}
            </Row>

            <span className="text-sm font-medium">{clientName}</span>

            <Row className="mt-1 phone:flex-wrap phone:gap-3">
              <FreshdeskTicketTag
                ticket={ticket}
                type="outlined"
                className="border-0 px-0 text-inherit"
              />

              <ProjectTypeTag
                projectType={type}
                type="outlined"
                className="border-0 px-0 text-inherit"
              />

              <Tag
                type="outlined"
                color="border"
                className="border-0 px-0 text-xs text-inherit"
                name={
                  <>
                    <CalendarIcon size={16} />
                    <ExpectedDate date={expectedDate} isActive={isProjectActive(status)} />
                  </>
                }
              />
            </Row>
          </Column>
        </Row>

        <Column
          gap="2"
          className="mid_tablet:col-span-5 mid_tablet:row-start-2 mid_tablet:flex-row mid_tablet:justify-between phone:row-start-3 phone:flex-col"
        >
          <Row gap="2" className="mid_tablet:justify-end phone:justify-start">
            <UserAvatarGroup users={[owner, subOwner]} size={32} avatarClassName="text-sm" />

            <EntityStatusTag
              target="project"
              status={status}
              className={'hidden h-auto w-fit shrink px-2 py-1 mid_tablet:flex'}
            />

            <span className="mid_tablet:hidden">{getFirstAndLastName(owner.name)}</span>
          </Row>

          <Row gap="2" className="mid_tablet:justify-end phone:flex-row-reverse">
            <EntityStatusTag
              target="invoiceControl"
              status={control.status}
              className={'hidden h-auto w-fit shrink px-2 py-1 mid_tablet:flex'}
            />

            <UserAvatarGroup
              users={[control.owner, control.subOwner]}
              size={32}
              avatarClassName="text-sm"
            />

            <span className="mid_tablet:hidden">{getFirstAndLastName(control.owner.name)}</span>
          </Row>
        </Column>

        <div className="justify-self-center mid_tablet:col-span-2 mid_tablet:justify-self-end phone:col-span-5 phone:row-start-2 phone:justify-self-start">
          <StatusDetails control={control} />
        </div>

        <Column gap="2" className="w-full mid_tablet:hidden" alignItems="flex-end">
          <EntityStatusTag
            target="project"
            status={status}
            className={'h-8 w-40 shrink px-1 phone:h-auto phone:w-fit'}
          />

          <EntityStatusTag
            target="invoiceControl"
            status={control.status}
            className={'h-8 w-40 shrink px-1 phone:h-auto phone:w-fit'}
          />
        </Column>
      </div>
    </Card>
  );
}

export default SingleProject;
